import { postAPI } from '@/services-ts/axios'
import type { ICompanyPostSetting, ICompanyPostSettingValue } from '@/types/Data'
import type {
  IDateRange,
  IGetEmployeeByPostType,
  IGetPostList,
  IGetPostReportList,
  IPayloadPostCard,
  IPayloadPostSetting,
  IUpdateFrame,
  IUpdatePostReport,
} from '@/types/Request'
import type { IPostStore } from '@/types/Store/State'
import { Alert, Format } from '@/utils-ts'
import { defineStore } from 'pinia'

export const usePostStore = defineStore('post', {
  state: (): IPostStore => ({
    companyPostSettings: [],
    post: null,
    postCardList: [],
    postReportList: {
      data: [],
      total: 0,
    },
  }),

  actions: {
    async GetPostList(payload: IGetPostList) {
      const { status, data } = await postAPI.PostServices.GetPostList(payload)
      this.post = status ? data : null
    },

    async GetFrameList() {
      const { status, data } = await postAPI.PostServices.GetFrameList()
      return status ? data : []
    },

    async UpdateFrame(data: IUpdateFrame) {
      const { status } = await postAPI.PostServices.UpdateFrame(data)

      if (status) {
        void Alert({ message: Format('admin_global_updated'), type: 'success' })
      }
    },

    async GetCompanyPostSettingList(IsUpdate: boolean = false) {
      if (this.companyPostSettings.length === 0 || IsUpdate) {
        const payload = {
          Id: null,
          Skip: 0,
          Take: 9999,
          IsAdmin: true,
        }
        const { status, data } = await postAPI.PostServices.GetCompanyPostSettingList(payload)
        this.companyPostSettings = status ? data : []
      }
    },

    async CreateCompanyPostSetting(payload: IPayloadPostSetting) {
      const response = await postAPI.PostServices.CreateCompanyPostSetting(payload)

      if (response.status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
        await this.GetCompanyPostSettingList(true)
      }
      return response
    },

    async UpdateCompanyPostSetting(payload: IPayloadPostSetting) {
      const response = await postAPI.PostServices.UpdateCompanyPostSetting(payload)

      if (response.status) {
        void Alert({
          message: Format('admin_global_updated'),
          type: 'success',
        })
        await this.GetCompanyPostSettingList(true)
      }
      return response
    },

    async DeleteCompanyPostSetting(Id: string) {
      const { status } = await postAPI.PostServices.DeleteCompanyPostSetting(Id)

      if (status) {
        void Alert({ message: Format('admin_global_deleted'), type: 'success' })
        await this.GetCompanyPostSettingList(true)
      }
      return status
    },

    async GetPostCardList() {
      const { status, data } = await postAPI.PostServices.GetPostCardList()
      this.postCardList = status ? data || [] : []
    },

    async CreatePostCard(payload: IPayloadPostCard) {
      const { status } = await postAPI.PostServices.CreatePostCard(payload)

      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
        await this.GetPostCardList()
      }
      return status
    },

    async UpdatePostCard(payload: IPayloadPostCard) {
      const { status } = await postAPI.PostServices.UpdatePostCard(payload)

      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
        await this.GetPostCardList()
      }
      return status
    },

    async DeletePostCard(Id: string) {
      const { status } = await postAPI.PostServices.DeletePostCard(Id)

      if (status) {
        this.postCardList = this.postCardList.filter(x => x.id !== Id)
        void Alert({ message: Format('admin_global_deleted'), type: 'success' })
      }
    },

    async GetPostReportList(payload: IGetPostReportList) {
      const { status, data, total } = await postAPI.PostServices.GetPostReportList(payload)
      this.postReportList = status
        ? { data: data || [], total: total || 0 }
        : { data: [], total: 0 }
    },

    async UpdatePostReport(payload: IUpdatePostReport) {
      const { status } = await postAPI.PostServices.UpdatePostReport(payload)
      return status
    },

    async GetEmployeeByPostType(payload: IGetEmployeeByPostType) {
      const { status, data } = await postAPI.PostServices.GetEmployeeByPostType(payload)
      return status ? data : null
    },

    async GetEmployeePostTypeExcel(payload: IDateRange) {
      await postAPI.PostServices.GetEmployeePostTypeExcel(payload)
    },

    async DeleteComment(CommentId: string) {
      const { status } = await postAPI.PostServices.DeleteComment(CommentId)
      return status
    },
  },

  getters: {
    GetPointSettings(): ICompanyPostSettingValue[] {
      return this.companyPostSettings.find(item => item.key === 'point')?.values ?? []
    },

    GetIsPointRestriction(): ICompanyPostSettingValue {
      const setting = extractDataByKey(this.companyPostSettings, 'IsPointRestriction')
      if (setting) {
        setting.value = JSON.parse(setting.value.toString().toLowerCase())
        return setting
      } else {
        return { value: false, id: '', companyId: '', key: 'IsPointRestriction', description: null }
      }
    },

    GetAllowedVideoUrls(): ICompanyPostSettingValue[] {
      return this.companyPostSettings.find(item => item.key === 'AllowVideoUrl')?.values ?? []
    },

    GetPostMaxValue(): ICompanyPostSettingValue {
      return getSettingValue(this.companyPostSettings, 'PostMax', {
        id: '',
        companyId: '',
        key: 'PostMax',
        value: '0',
        description: null,
      })
    },

    GetPostMinValue(): ICompanyPostSettingValue {
      return getSettingValue(this.companyPostSettings, 'PostMin', {
        id: '',
        companyId: '',
        key: 'PostMin',
        value: '0',
        description: null,
      })
    },

    GetAnimationShowRate(): ICompanyPostSettingValue {
      return getSettingValue(this.companyPostSettings, 'AnimationShowRate', {
        id: '',
        companyId: '',
        key: 'AnimationShowRate',
        value: '0',
        description: null,
      })
    },

    GetDefaultPostTypeId(): ICompanyPostSettingValue {
      return getSettingValue(this.companyPostSettings, 'DefaultPostTypeId', {
        id: '',
        companyId: '',
        key: 'DefaultPostTypeId',
        value: '',
        description: null,
      })
    },

    GetShowActionsOnPinPosts(): ICompanyPostSettingValue {
      return getSettingValue(this.companyPostSettings, 'ShowActionsOnPinPosts', {
        id: '',
        companyId: '',
        key: 'ShowActionsOnPinPosts',
        value: 'false',
        description: null,
      })
    },

    GetAttachmentIsMandatory(): ICompanyPostSettingValue {
      return getSettingValue(this.companyPostSettings, 'AttachmentIsMandatory', {
        id: '',
        companyId: '',
        key: 'AttachmentIsMandatory',
        value: 'false',
        description: null,
      })
    },

    GetValueMandatoryOnThanksPosts(): ICompanyPostSettingValue {
      return getSettingValue(this.companyPostSettings, 'ValueMandatoryOnThanksPosts', {
        id: '',
        companyId: '',
        key: 'ValueMandatoryOnThanksPosts',
        value: 'false',
        description: null,
      })
    },

    GetSendMailToChief(): ICompanyPostSettingValue {
      return getSettingValue(this.companyPostSettings, 'SendMailToChief', {
        id: '',
        companyId: '',
        key: 'SendMailToChief',
        value: 'false',
        description: null,
      })
    },

    GetHideInteractionsDetail(): ICompanyPostSettingValue {
      return getSettingValue(this.companyPostSettings, 'HideInteractionsDetail', {
        id: '',
        companyId: '',
        key: 'HideInteractionsDetail',
        value: 'false',
        description: null,
      })
    },

    GetShowUploadPhotoPopup(): ICompanyPostSettingValue {
      return getSettingValue(this.companyPostSettings, 'ShowUploadPhotoPopup', {
        id: '',
        companyId: '',
        key: 'ShowUploadPhotoPopup',
        value: 'false',
        description: null,
      })
    },

    GetMaximumPointValue() {
      const maximumPointValue = getSettingValue(this.companyPostSettings, 'MaximumPointValue', {
        id: '',
        companyId: '',
        key: 'MaximumPointValue',
        value: '',
        description: null,
      })
      if (Object.values(maximumPointValue).length === 0) {
        return { value: 0, id: null }
      }
      return maximumPointValue
    },

    GetShowCommentLikeNotifications(): ICompanyPostSettingValue {
      return getSettingValue(this.companyPostSettings, 'ShowCommentLikeNotifications', {
        id: '',
        companyId: '',
        key: 'ShowCommentLikeNotifications',
        value: 'false',
        description: null,
      })
    },

    GetAllowPostCommunityFromHome(): ICompanyPostSettingValue {
      return getSettingValue(this.companyPostSettings, 'AllowPostCommunityFromHome', {
        id: '',
        companyId: '',
        key: 'AllowPostCommunityFromHome',
        value: 'false',
        description: null,
      })
    },

    GetIsStoryModeActive(): ICompanyPostSettingValue {
      return getSettingValue(this.companyPostSettings, 'IsStoryModeActive', {
        id: '',
        companyId: '',
        key: 'IsStoryModeActive',
        value: 'false',
        description: null,
      })
    },
  },
})

function extractDataByKey(data: ICompanyPostSetting[], keyToFind: string) {
  const foundItem = data.find(item => item.key === keyToFind)
  if (foundItem) {
    return foundItem.values[0]
  }
  return null
}

function getSettingValue(
  data: ICompanyPostSetting[],
  keyToFind: string,
  defaultValue: ICompanyPostSettingValue,
): ICompanyPostSettingValue {
  return extractDataByKey(data, keyToFind) ?? defaultValue
}
